import { FocusEvent, KeyboardEvent, RefObject } from "react";

type Focusable = HTMLInputElement | HTMLTextAreaElement;
// RefObject<unknown>: some item pickers type their ref as a react-select instance while holding the MUI input.
type Target = RefObject<unknown> | Element | null | undefined;

const resolve = (target: Target): Focusable | null => {
	const element = target && "current" in target ? target.current : target;
	return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement ? element : null;
};

/** Select the text of an input; number inputs and some types refuse setSelectionRange but accept select(). */
export const selectContents = (element: Element | null | undefined) => {
	if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
		try {
			element.select();
		} catch {
			// Input types without a text selection: focus is enough.
		}
	}
};

/**
 * Focus and select a field after React has committed the current update.
 *
 * Calling focus() right after setState fails two ways: the field may still be
 * disabled (it enables on the next render), or its value is rewritten by the
 * commit, which drops the selection. A field that already has focus fires no
 * focus event either, so select() is called directly here.
 */
export const focusAndSelect = (target: Target, attempts = 5) => {
	requestAnimationFrame(() => {
		const element = resolve(target);
		if (!element) return;
		if (element.disabled && attempts > 0) {
			focusAndSelect(target, attempts - 1);
			return;
		}
		element.focus();
		selectContents(element);
	});
};

/** onFocus handler: select the whole value so typing replaces it. */
export const selectOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
	const element = event.target;
	// Deferred: a mouse click would otherwise place the caret and undo the selection.
	requestAnimationFrame(() => {
		if (document.activeElement === element) selectContents(element);
	});
};

/** Plain Enter, without modifiers or an IME composition in progress. */
export const isPlainEnter = (event: KeyboardEvent) =>
	event.key === "Enter" && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey && !event.nativeEvent.isComposing;
